import { EventOpportunity } from "../../services/eventOpportunitiesApi";

export type DraftDay = { date: string; startTime: string; endTime: string };

export type EventDraft = {
  title: string;
  location: string;
  days: DraftDay[];
  /** The listing spans weeks, so only one day is suggested. */
  isSeason: boolean;
  /** Hours weren't listed; the defaults need checking. */
  timesEstimated: boolean;
  /** The listing says its dates are estimated. */
  datesEstimated: boolean;
};

const DEFAULT_START = "9:00 AM";
const DEFAULT_END = "3:00 PM";
const MAX_DAYS = 31;

const WEEKDAY_PATTERN =
  /\b(sun(?:day)?s?|mon(?:day)?s?|tue(?:s(?:day)?)?s?|wed(?:nesday)?s?|thu(?:r(?:s(?:day)?)?)?s?|fri(?:day)?s?|sat(?:urday)?s?)\b/i;
const WEEKDAYS = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"];

export const weekdayFromText = (text?: string): number | null => {
  if (!text || /\b(daily|every ?day)\b/i.test(text)) {
    return null;
  }
  const match = text.match(WEEKDAY_PATTERN);
  return match ? WEEKDAYS.indexOf(match[1].slice(0, 3).toLowerCase()) : null;
};

export const to12Hour = (hours24: number, minutes: number): string => {
  const suffix = hours24 >= 12 ? "PM" : "AM";
  const hour = hours24 % 12 === 0 ? 12 : hours24 % 12;
  return `${hour}:${String(minutes).padStart(2, "0")} ${suffix}`;
};

export const timeToMinutes = (time: string): number | null => {
  const match = time.trim().match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
  if (!match) {
    return null;
  }
  let hours = Number(match[1]);
  if (hours < 1 || hours > 12 || Number(match[2]) > 59) {
    return null;
  }
  if (match[3].toUpperCase() === "AM") {
    hours = hours === 12 ? 0 : hours;
  } else if (hours !== 12) {
    hours += 12;
  }
  return hours * 60 + Number(match[2]);
};

const clock = (hour: string, minute: string | undefined, suffix: string) => {
  let hours = Number(hour) % 12;
  if (suffix.toLowerCase() === "pm") {
    hours += 12;
  }
  return to12Hour(hours, Number(minute || 0));
};

/** Reads "10:00 AM - 1:00 PM", "2 PM-6 PM", "08:00" or "5:00 PM". */
export const parseTimeRange = (
  text?: string,
): { start: string; end?: string } | null => {
  if (!text) {
    return null;
  }
  const range = text.match(
    /(\d{1,2})(?::(\d{2}))?\s*(am|pm)?\s*(?:-|–|to)\s*(\d{1,2})(?::(\d{2}))?\s*(am|pm)\b/i,
  );
  if (range) {
    const endSuffix = range[6];
    let start = clock(range[1], range[2], range[3] || endSuffix);
    const end = clock(range[4], range[5], endSuffix);
    // "10-1 PM": the start can't be after the end, so it is the morning.
    if (!range[3] && timeToMinutes(start)! > timeToMinutes(end)!) {
      start = clock(range[1], range[2], "am");
    }
    return { start, end };
  }
  const single = text.match(/\b(\d{1,2}):(\d{2})\s*(am|pm)\b/i);
  if (single) {
    return { start: clock(single[1], single[2], single[3]) };
  }
  const twentyFour = text.trim().match(/^(\d{2}):(\d{2})$/);
  if (twentyFour && Number(twentyFour[1]) < 24) {
    return { start: to12Hour(Number(twentyFour[1]), Number(twentyFour[2])) };
  }
  return null;
};

const localDateKey = (date: Date) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(
    2,
    "0",
  )}-${String(date.getDate()).padStart(2, "0")}`;

const keyToUtc = (key: string) => {
  const [year, month, day] = key.split("-").map(Number);
  return Date.UTC(year, month - 1, day);
};

export const addDays = (key: string, days: number) => {
  const date = new Date(keyToUtc(key) + days * 86400000);
  return date.toISOString().slice(0, 10);
};

const weekdayOf = (key: string) => new Date(keyToUtc(key)).getUTCDay();

const nextWeekday = (fromKey: string, weekday: number) =>
  addDays(fromKey, (weekday - weekdayOf(fromKey) + 7) % 7);

const defaultEndFor = (start: string) => {
  const minutes = timeToMinutes(start);
  if (minutes === null) {
    return DEFAULT_END;
  }
  const end = Math.min(minutes + 240, 23 * 60 + 59);
  return to12Hour(Math.floor(end / 60), end % 60);
};

const locationFor = (opportunity: EventOpportunity) => {
  const city = opportunity.city?.trim();
  const address = opportunity.address?.trim();
  const parts = [opportunity.venueName, address];
  // Imported addresses usually already end with the city and state.
  if (
    !city ||
    !address ||
    !address.toLowerCase().includes(city.toLowerCase())
  ) {
    parts.push(city, opportunity.state);
  }
  return parts.filter(Boolean).join(", ");
};

/**
 * Suggests what to put on the vendor's calendar for a discovered event.
 * A short event (a weekend festival) gets every day. A long listing (a weekly
 * market all season) gets only the next matching day, since the vendor adds
 * the days they will actually work.
 */
export const buildEventDraft = (
  opportunity: EventOpportunity,
  now: Date = new Date(),
): EventDraft => {
  const schedule = (opportunity.requirements?.schedule || {}) as {
    text?: string;
    startTime?: string;
    datesEstimated?: boolean;
  };
  const startKey = opportunity.startAt.slice(0, 10);
  const endKey = (opportunity.endAt || opportunity.startAt).slice(0, 10);
  const todayKey = localDateKey(now);
  const spanDays = Math.round(
    (keyToUtc(endKey) - keyToUtc(startKey)) / 86400000,
  );
  const isSeason = spanDays > 7;

  const listed =
    parseTimeRange(schedule.startTime) || parseTimeRange(schedule.text);
  const startTime = listed?.start || DEFAULT_START;
  const endTime =
    listed?.end || (listed ? defaultEndFor(listed.start) : DEFAULT_END);
  const timesEstimated = !listed?.end;

  let dates: string[];
  if (opportunity.datesConfirmed === false) {
    // The listing's dates are old or missing: start from today and let the
    // vendor set the real ones.
    dates = [todayKey];
  } else if (isSeason) {
    const from = todayKey > startKey ? todayKey : startKey;
    const weekday =
      weekdayFromText(schedule.text) ?? weekdayFromText(schedule.startTime);
    const pick = weekday === null ? from : nextWeekday(from, weekday);
    dates = [pick <= endKey ? pick : from];
  } else {
    dates = [];
    for (
      let key = startKey;
      key <= endKey && dates.length < MAX_DAYS;
      key = addDays(key, 1)
    ) {
      dates.push(key);
    }
  }

  return {
    title: opportunity.name,
    location: locationFor(opportunity),
    days: dates.map((date) => ({ date, startTime, endTime })),
    isSeason: isSeason && opportunity.datesConfirmed !== false,
    timesEstimated,
    datesEstimated:
      Boolean(schedule.datesEstimated) || opportunity.datesConfirmed === false,
  };
};

export const draftDayHours = (day: DraftDay): number => {
  const start = timeToMinutes(day.startTime);
  const end = timeToMinutes(day.endTime);
  return start === null || end === null || end <= start
    ? 0
    : (end - start) / 60;
};

/** The per-day slots the API takes; null when any day has no valid hours. */
export const draftToSchedule = (days: DraftDay[]) => {
  if (days.length === 0 || days.some((day) => draftDayHours(day) <= 0)) {
    return null;
  }
  return days.map((day) => ({
    date: day.date,
    startTime: day.startTime,
    endTime: day.endTime,
    startDateTime: localDateTimeToIso(day.date, day.startTime),
    endDateTime: localDateTimeToIso(day.date, day.endTime),
    hours: draftDayHours(day),
  }));
};

/** "2027-06-06" + "10:00 AM" in the browser's time zone, as an ISO timestamp. */
export const localDateTimeToIso = (date: string, time: string): string => {
  const [year, month, day] = date.split("-").map(Number);
  const minutes = timeToMinutes(time) ?? 0;
  return new Date(
    year,
    month - 1,
    day,
    Math.floor(minutes / 60),
    minutes % 60,
  ).toISOString();
};

/** "10:00 AM" -> "10:00" for an <input type="time">. */
export const timeTo24 = (time: string): string => {
  const minutes = timeToMinutes(time);
  if (minutes === null) {
    return "";
  }
  return `${String(Math.floor(minutes / 60)).padStart(2, "0")}:${String(
    minutes % 60,
  ).padStart(2, "0")}`;
};

/** "13:00" from an <input type="time"> -> "1:00 PM". */
export const time24To12 = (value: string): string => {
  const match = value.match(/^(\d{2}):(\d{2})$/);
  return match ? to12Hour(Number(match[1]), Number(match[2])) : "";
};
